/* eslint-disable sort-keys */
import React, { createContext, FC, useContext, useEffect, useMemo, useRef, useState } from "react";
import ExceptionsService from "@app/exceptions/exceptionsService";
import { DropdownItem } from "@app/shared/checkbox-dropdown";
import { NestingParentDropdownItem } from "@app/shared/checkbox-dropdown/types";
import { ExceptionsApiFilters, ExceptionsFilterRequestModel } from "../interfaces";
import { SpecialtyGroupExceptionStatus } from "@app/projects/results/common";
import { calculateServiceAreaSelections } from "@app/shared/service-area-dropdown/helpers";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import {
  BulkFilterData,
  ServiceAreaFilterOptions,
  SortExceptionColumn,
  SpecialtyGroupExceptionGrade
} from "@app/projects/results/common/specialtyGroupException";
import { clone } from "lodash";
import { RegionSetStatusType } from "@app/projects/enumerators";
import { SortDirections } from "@app/shared/waffles/exports";
import { FilterSetItem } from "@app/resource-filters/filter-sets";
import MetOverallStatus from "@app/exceptions/metOverallStatusEnum";
import useGapFillStatusDescriptions from "@app/projects/network-assessment/adequacy/hooks/useGapFillStatusDescriptions";
import { GapFillStatusEnum } from "@app/projects/network-assessment/adequacy";
import startCase from "lodash/startCase";
import NetworkTaxonomyService from "@app/network-taxonomies/networkTaxonomyService";
import { NetworkTaxonomyLineOfBusinessSimple } from "@app/network-taxonomies/networkTaxonomy";
import UnprocessedExcludedProviders from "../unprocessedExcludedProviders";

export interface ExceptionsFiltersContextValues {
  apiReadyFilters: ExceptionsApiFilters;
  applyCurrentFilterSetItems: (filterSetItems: FilterSetItem[]) => void;
  applyFilters: () => void;
  applyDisabled: boolean;
  clearFilters: () => void;
  reloadFilters: () => void;
  exceptionStatusOptions: Array<DropdownItem<string>>,
  exceptionsLoading: boolean,
  filterEndDate: string;
  filterExceptionStatus: Array<DropdownItem<string>>;
  filterGapResolutionStatuses: Array<DropdownItem<string>>;
  filterExceptionGrades: Array<DropdownItem<string>>;
  filterLinesOfBusiness: Array<DropdownItem<string>>;
  filterProjects: Array<DropdownItem<string>>;
  filterServiceArea: string[];
  filterServiceAreaStatuses: Array<DropdownItem<string>>;
  filterSpecialty: Array<DropdownItem<string>>;
  filterStartDate: string;
  gapResolutionStatusOptions: Array<DropdownItem<string>>;
  exceptionGradeOptions: Array<DropdownItem<string>>;
  hasLoadedFilterOptions: boolean;
  hasSetInitialExceptionStatuses: boolean;
  isForArchived: boolean;
  lineOfBusinessOptions: Array<DropdownItem<string>>;
  metOverallStatus: MetOverallStatus;
  pageIndex: number;
  pageSize: number;
  projectsOptions: Array<DropdownItem<string>>;
  requestModel: ExceptionsFilterRequestModel;
  resetToFirstPage: boolean,
  selectedExceptionIds: number[];
  serviceAreaOptions: NestingParentDropdownItem[];
  serviceAreaStatusOptions: Array<DropdownItem<string>>;
  setExceptionsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setFilterEndDate: React.Dispatch<React.SetStateAction<string>>;
  setFilterExceptionStatus: React.Dispatch<React.SetStateAction<Array<DropdownItem<string>>>>;
  setFilterGapResolutionStatuses: React.Dispatch<React.SetStateAction<Array<DropdownItem<string>>>>;
  setFilterExceptionGrades: React.Dispatch<React.SetStateAction<Array<DropdownItem<string>>>>;
  setFilterProjects: React.Dispatch<React.SetStateAction<Array<DropdownItem<string>>>>;
  setFilterLinesOfBusiness: React.Dispatch<React.SetStateAction<Array<DropdownItem<string>>>>;
  setFilterServiceArea: React.Dispatch<React.SetStateAction<string[]>>;
  setFilterServiceAreaStatuses: React.Dispatch<React.SetStateAction<Array<DropdownItem<string>>>>;
  setFilterSpecialty: React.Dispatch<React.SetStateAction<Array<DropdownItem<string>>>>;
  setFilterStartDate: React.Dispatch<React.SetStateAction<string>>;
  setHasLoadedFilterOptions: React.Dispatch<React.SetStateAction<boolean>>;
  setHasSetInitialExceptionStatuses: React.Dispatch<React.SetStateAction<boolean>>;
  setIsForArchived: React.Dispatch<React.SetStateAction<boolean>>;
  setMetOverallStatus: React.Dispatch<React.SetStateAction<MetOverallStatus>>;
  setPageIndex: React.Dispatch<React.SetStateAction<number>>;
  setPageSize: React.Dispatch<React.SetStateAction<number>>;
  setResetToFirstPage: React.Dispatch<React.SetStateAction<boolean>>;
  setSortColumn: React.Dispatch<React.SetStateAction<SortExceptionColumn>>;
  setSortDirection: React.Dispatch<React.SetStateAction<SortDirections>>;
  setUpdateFilters: React.Dispatch<React.SetStateAction<boolean>>;
  setRequestModel: React.Dispatch<React.SetStateAction<ExceptionsFilterRequestModel>>;
  setSelectedExceptionIds: React.Dispatch<React.SetStateAction<number[]>>;
  setTriggerRefreshData: React.Dispatch<React.SetStateAction<boolean>>;
  setUnprocessedExcludedProviders: React.Dispatch<React.SetStateAction<UnprocessedExcludedProviders>>;
  sortColumn: SortExceptionColumn;
  sortDirection: SortDirections
  specialtyOptions: Array<DropdownItem<string>>;
  triggerRefreshData: boolean;
  unprocessedExcludedProviders: UnprocessedExcludedProviders;
  validStartDate: boolean;
  validEndDate: boolean;
  customProjectName: string[];
  setCustomProjectName: (names: string[]) => void;
  enableRegulatoryExceptionReports: boolean[];
  setEnableRegulatoryExceptionReports: (values: boolean[]) => void;
  haveRemovedServiceArea: boolean[];
  setHaveRemovedServiceArea: (values: boolean[]) => void;
  haveRemovedSpecialty: boolean[];
  setHaveRemovedSpecialty: (values: boolean[]) => void;
}

const initialValues: ExceptionsFiltersContextValues = {
  apiReadyFilters: null,
  applyCurrentFilterSetItems: () => null,
  applyFilters: () => null,
  applyDisabled: false,
  clearFilters: () => null,
  reloadFilters: () => null,
  exceptionStatusOptions: new Array<DropdownItem<string>>(),
  exceptionsLoading: false,
  filterEndDate: '',
  filterExceptionStatus: new Array<DropdownItem<string>>(),
  filterGapResolutionStatuses: new Array<DropdownItem<string>>(),
  filterExceptionGrades: new Array<DropdownItem<string>>(),
  filterLinesOfBusiness: new Array<DropdownItem<string>>(),
  filterProjects: new Array<DropdownItem<string>>(),
  filterServiceArea: [],
  filterServiceAreaStatuses: [],
  filterSpecialty: new Array<DropdownItem<string>>(),
  filterStartDate: '',
  gapResolutionStatusOptions: new Array<DropdownItem<string>>(),
  exceptionGradeOptions: new Array < DropdownItem<string>>(),
  hasLoadedFilterOptions: false,
  hasSetInitialExceptionStatuses: false,
  isForArchived: false,
  lineOfBusinessOptions: new Array<DropdownItem<string>>(),
  metOverallStatus: MetOverallStatus.All,
  pageIndex: null,
  pageSize: null,
  projectsOptions: null,
  resetToFirstPage: null,
  requestModel: null,
  selectedExceptionIds: [],
  serviceAreaOptions: [],
  serviceAreaStatusOptions: [],
  setExceptionsLoading: () => null,
  setFilterEndDate: () => null,
  setFilterExceptionStatus: () => null,
  setFilterGapResolutionStatuses: () => null,
  setFilterExceptionGrades: () => null,
  setFilterLinesOfBusiness: () => null,
  setFilterProjects: () => null,
  setFilterServiceArea: () => null,
  setFilterServiceAreaStatuses: () => null,
  setFilterSpecialty: () => null,
  setFilterStartDate: () => null,
  setHasLoadedFilterOptions: () => null,
  setHasSetInitialExceptionStatuses: () => null,
  setIsForArchived: () => null,
  setMetOverallStatus: () => null,
  setPageIndex: () => null,
  setPageSize: () => null,
  setResetToFirstPage: () => null,
  setRequestModel: () => null,
  setSortColumn: () => null,
  setSortDirection: () => null,
  setUpdateFilters: () => null,
  setSelectedExceptionIds: () => null,
  setTriggerRefreshData: () => null,
  setUnprocessedExcludedProviders: () => null,
  sortColumn: SortExceptionColumn.DateUpdated,
  sortDirection: SortDirections.Desc,
  specialtyOptions: new Array<DropdownItem<string>>(),
  triggerRefreshData: true,
  unprocessedExcludedProviders: UnprocessedExcludedProviders.All,
  validStartDate: true,
  validEndDate: true,
  customProjectName: [],
  setCustomProjectName: () => {
  },
  enableRegulatoryExceptionReports: [],
  setEnableRegulatoryExceptionReports: () => {
  },
  haveRemovedServiceArea: [],
  setHaveRemovedServiceArea: () => {},
  haveRemovedSpecialty: [],
  setHaveRemovedSpecialty: () => {},
}

export const ExceptionsFiltersContext = createContext(initialValues);

const ExceptionsFiltersContextProvider: FC<{ children: React.ReactNode }> = (props: { children: React.ReactNode }) => {
  /* Constants */
  const defaultExceptionStatuses = useRef([
    SpecialtyGroupExceptionStatus.Pending,
    SpecialtyGroupExceptionStatus.Submitted,
    SpecialtyGroupExceptionStatus.Approved,
    SpecialtyGroupExceptionStatus.Denied,
    SpecialtyGroupExceptionStatus.Appealed,
    SpecialtyGroupExceptionStatus.Expired
  ]).current;

  const defaultServiceAreaStatuses = useRef([
    RegionSetStatusType.Active,
    RegionSetStatusType.Approved,
    RegionSetStatusType.Pending,
    RegionSetStatusType.Opportunity,
    null
  ]).current;

  const defaultGapResolutionStatuses = useRef([
    GapFillStatusEnum.none,
    GapFillStatusEnum.gapFilled,
    GapFillStatusEnum.researching,
    GapFillStatusEnum.contractingGapClosure,
    GapFillStatusEnum.contractingExceptionEligible,
    GapFillStatusEnum.exceptionRequired,
    GapFillStatusEnum.rppoWaiverRequired,
    GapFillStatusEnum.availableEntitiesWillNotContract
  ]).current;

  const defaultExceptionGrades = useRef([
    SpecialtyGroupExceptionGrade.None,
    SpecialtyGroupExceptionGrade.A,
    SpecialtyGroupExceptionGrade.B,
    SpecialtyGroupExceptionGrade.C
  ]).current;

  const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;
  const exceptionsService = useRef(new ExceptionsService()).current;
  const networkTaxonomyService = useRef(new NetworkTaxonomyService()).current;
  const gapFillStatusDescriptions = useGapFillStatusDescriptions();

  /* Filter Options */
  // We have a whole bunch of arrays in state here
  // Be extra careful to not modify any of these in place
  // because it will make it difficult to troubleshoot anything
  const [serviceAreaOptions, setServiceAreaOptions] = useState<NestingParentDropdownItem[]>([]);
  const [serviceAreaStatusOptions, setServiceAreaStatusOptions] = useState<Array<DropdownItem<string>>>([]);
  const [specialtyOptions, setSpecialtyOptions] = useState<Array<DropdownItem<string>>>([]);
  const [exceptionStatusOptions, setExceptionStatusOptions] = useState<Array<DropdownItem<string>>>([]);
  const [projectsOptions, setProjectsOptions] = useState<Array<DropdownItem<string>>>([]);
  const [lineOfBusinessOptions, setLineOfBusinessOptions] = useState<Array<DropdownItem<string>>>([]);
  const gapResolutionStatusOptions: DropdownItem<string>[] = defaultGapResolutionStatuses.map(status => ({ key: status, name: gapFillStatusDescriptions[status] }));
  const exceptionGradeOptions: DropdownItem<string>[] = defaultExceptionGrades.map(grade => ({ key: grade, name: startCase(grade) }));

  /* Selected Filter Values (and Related) */
  const [filterServiceArea, setFilterServiceArea] = useState<string[]>(initialValues.filterServiceArea);
  const [filterServiceAreaStatuses, setFilterServiceAreaStatuses] = useState<Array<DropdownItem<string>>>(initialValues.filterServiceAreaStatuses);
  const [filterSpecialty, setFilterSpecialty] = useState<Array<DropdownItem<string>>>(initialValues.filterSpecialty);
  const [filterExceptionStatus, setFilterExceptionStatus] = useState<Array<DropdownItem<string>>>(initialValues.filterExceptionStatus);
  const [filterGapResolutionStatuses, setFilterGapResolutionStatuses] = useState<Array<DropdownItem<string>>>(initialValues.filterGapResolutionStatuses);
  const [filterExceptionGrades, setFilterExceptionGrades] = useState<Array<DropdownItem<string>>>(initialValues.filterExceptionGrades);
  const [filterProjects, setFilterProjects] = useState<Array<DropdownItem<string>>>(initialValues.filterProjects);
  const [filterLinesOfBusiness, setFilterLinesOfBusiness] = useState<Array<DropdownItem<string>>>(initialValues.filterLinesOfBusiness);
  const [filterStartDate, setFilterStartDate] = useState<string>(initialValues.filterStartDate);
  const [filterEndDate, setFilterEndDate] = useState<string>(initialValues.filterEndDate);
  const [validStartDate, setValidStartDate] = useState<boolean>(true);
  const [validEndDate, setValidEndDate] = useState<boolean>(true);
  const [applyDisabled, setApplyDisabled] = useState<boolean>(false);
  const [apiReadyFilters, setApiReadyFilters] = useState<ExceptionsApiFilters>(initialValues.apiReadyFilters);
  const [metOverallStatus, setMetOverallStatus] = useState<MetOverallStatus>(MetOverallStatus.All);
  const [unprocessedExcludedProviders, setUnprocessedExcludedProviders] = useState<UnprocessedExcludedProviders>(UnprocessedExcludedProviders.All);

  /* Miscellaneous */
  const [exceptionsLoading, setExceptionsLoading] = useState(initialValues.exceptionsLoading);
  const [requestModel, setRequestModel] = useState<ExceptionsFilterRequestModel>(null);
  const [triggerRefreshData, setTriggerRefreshData] = useState<boolean>(true);
  const [isForArchived, setIsForArchived] = useState<boolean>(false);
  const [updateFilters, setUpdateFilters] = useState<boolean>(false);
  const [pageIndex, setPageIndex] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(100);
  const [resetToFirstPage, setResetToFirstPage] = useState<boolean>(false);
  const [sortDirection, setSortDirection] = useState<SortDirections>(SortDirections.Desc);
  const [sortColumn, setSortColumn] = useState<SortExceptionColumn>(SortExceptionColumn.DateUpdated);

  /* Internal State Values */
  const [hasSetInitialExceptionStatuses, setHasSetInitialExceptionStatuses] = useState(false);
  const [hasLoadedFilterOptions, setHasLoadedFilterOptions] = useState(false);
  const [selectedExceptionIds, setSelectedExceptionIds] = useState<number[]>([]);
  const [customProjectName, setCustomProjectName] = useState<string[]>([]);
  const [enableRegulatoryExceptionReports, setEnableRegulatoryExceptionReports] = useState<boolean[]>([]);
  const [haveRemovedServiceArea, setHaveRemovedServiceArea] = useState<boolean[]>([]);
  const [haveRemovedSpecialty, setHaveRemovedSpecialty] = useState<boolean[]>([]);

  useEffect(() => {
    const fetchFilterOptions = async () => {
      const filterData: BulkFilterData = await exceptionsService.getFilterOptions(isForArchived);

      // We only need to load the lines of business once; they won't change while a user is clicking around the page
      if (lineOfBusinessOptions.length === 0) {
        const linesOfBusiness = await networkTaxonomyService.getClientFriendlyLinesOfBusiness();
        setLineOfBusinessOptions(convertLinesOfBusinessToDropdownItems(linesOfBusiness));
      }

      const projects = convertProjectsToDropdownItems(filterData.bulkProjects);
      setProjectsOptions(projects as DropdownItem<string>[]);

      const specialties = convertSpecialtiesToDropdownItems(filterData.bulkSpecialties);
      setSpecialtyOptions(specialties as DropdownItem<string>[]);

      const serviceAreas: NestingParentDropdownItem[] = convertServiceAreasToNestingParentDropdownItems(filterData.bulkServiceAreas);
      setServiceAreaOptions(serviceAreas as NestingParentDropdownItem[]);

      const serviceAreaStatuses = defaultServiceAreaStatuses.map(status => ({
        key: status,
        name: status == null ? 'Blank' : status.charAt(0).toUpperCase() + status.slice(1)
      } as DropdownItem<string>));
      setServiceAreaStatusOptions(serviceAreaStatuses as DropdownItem<string>[]);

      const defaultStatuses = clone(defaultExceptionStatuses);
      if (isForArchived) {
        //Add the None (empty) option to the first position of the array, so that it gets displayed first
        defaultStatuses.unshift(SpecialtyGroupExceptionStatus.None);
      }

      const exceptionStatusOptions = defaultStatuses.map(status => ({
        key: status,
        name: status.charAt(0).toUpperCase() + status.slice(1)
      } as DropdownItem<string>));
      setExceptionStatusOptions(exceptionStatusOptions as DropdownItem<string>[]);

      setHasLoadedFilterOptions(true);
      setUpdateFilters(false);
    }
    fetchFilterOptions();
  }, [featureFlags, isForArchived, updateFilters, gapFillStatusDescriptions]);

  useEffect(() => {
    if (hasLoadedFilterOptions) {
      const defaults = defaultFilterSelections;

      const defaultFilters: ExceptionsApiFilters = {
        projectIds: defaults.selectedProjects.map((p) => parseInt(p.key, 10)),
        counties: calculateServiceAreaSelections(serviceAreaOptions, defaults.selectedServiceAreas, true).counties,
        specialties: defaults.selectedSpecialties.map((sp) => sp.key),
        startDate: defaults.selectedStartDate,
        endDate: defaults.selectedEndDate,
        statuses: defaults.selectedStatuses.map((s) => s.key as SpecialtyGroupExceptionStatus),
        gapResolutionStatuses: defaults.selectedGapResolutionStatuses.map(grs => grs.key),
        exceptionGrades: defaults.selectedExceptionGrades.map(eg => eg.key as SpecialtyGroupExceptionGrade),
        lineOfBusinessIds: defaults.selectedLinesOfBusiness.map(line => parseInt(line.key))
      };

      setApiReadyFilters(defaultFilters);

      setFilterServiceArea(defaults.selectedServiceAreas);
      setFilterServiceAreaStatuses(defaults.selectedServiceAreaStatuses);
      setFilterSpecialty(defaults.selectedSpecialties);
      setFilterExceptionStatus(defaults.selectedStatuses);
      setFilterProjects(defaults.selectedProjects);
      setFilterStartDate(defaults.selectedStartDate);
      setFilterEndDate(defaults.selectedEndDate);
      setHasSetInitialExceptionStatuses(true);
      setFilterGapResolutionStatuses(defaults.selectedGapResolutionStatuses);
      setFilterExceptionGrades(defaults.selectedExceptionGrades);
      setFilterLinesOfBusiness(defaults.selectedLinesOfBusiness);
    }
  }, [hasLoadedFilterOptions]);

  // because we auto-set exception status filters on load, we have to apply them
  useEffect(() => {
    if (hasSetInitialExceptionStatuses && filterExceptionStatus.length > 0) {
      applyFilters();
    }
  }, [hasSetInitialExceptionStatuses]);

  useEffect(() => {
    if (hasSetInitialExceptionStatuses && filterExceptionStatus.length > 0) {
      applyFilters();
    }
  }, [pageIndex, pageSize, sortDirection, sortColumn]);

  // if we've got a start date with no end date, or a start date that's before the end date, or an empty start date, it's valid
  useEffect(() => {
    if ((filterStartDate && !filterEndDate) || (filterStartDate && filterEndDate && filterStartDate < filterEndDate) || (!filterStartDate)) {
      setValidStartDate(true);
    } else {
      setValidStartDate(false);
    }
  }, [filterStartDate, filterEndDate]);

  // if we've got an end date with no start date, or an end date that's after the start date, or an empty end date, it's valid
  useEffect(() => {
    if ((filterEndDate && !filterStartDate) || (filterEndDate && filterStartDate && filterEndDate > filterStartDate) || (!filterEndDate)) {
      setValidEndDate(true);
    } else {
      setValidEndDate(false);
    }
  }, [filterEndDate, filterStartDate]);

  useEffect(() => {
    if (filterProjects.length === 0
      || filterServiceArea.length === 0
      || filterServiceAreaStatuses.length === 0
      || filterSpecialty.length === 0
      || !isForArchived && filterExceptionStatus.length === 0
      || filterGapResolutionStatuses.length === 0
      || filterExceptionGrades.length === 0
      || filterLinesOfBusiness.length === 0
      || !validStartDate
      || !validEndDate) {
      setApplyDisabled(true)
    } else {
      setApplyDisabled(false);
    }
  }, [filterProjects, filterServiceArea, filterServiceAreaStatuses, filterSpecialty, filterExceptionStatus, validStartDate, validEndDate, filterGapResolutionStatuses, filterExceptionGrades, filterLinesOfBusiness]);

  const convertProjectsToDropdownItems = (obj: { [key: string]: string }): DropdownItem<string>[] => {
    return Object.entries(obj).map(([key, value]) => ({
      key,
      name: value
    }));
  }

  const convertSpecialtiesToDropdownItems = (arr: { key: string; value: string }[]): DropdownItem<string>[] => {
    return arr.map(item => ({
      key: item.key,
      name: item.value,
    }));
  };

  const convertLinesOfBusinessToDropdownItems = (linesOfBusiness: NetworkTaxonomyLineOfBusinessSimple[]): DropdownItem<string>[] => {
    return linesOfBusiness.map(line => ({
        key: line.lineOfBusinessId + "",
        name: line.name
      }));
  }

  const convertServiceAreasToNestingParentDropdownItems = (data: {
    [stateAbbreviation: string]: ServiceAreaFilterOptions[]
  }): NestingParentDropdownItem[] => {
    return Object.keys(data).map(stateAbbreviation => ({
      key: stateAbbreviation,
      name: stateAbbreviation,
      checkboxStatus: null,
      children: data[stateAbbreviation].map(serviceArea => ({
        key: serviceArea.countyRegionSetId.toString(),
        name: !!serviceArea.countySSA ? serviceArea.countyDisplayName + ' - ' + serviceArea.countySSA : serviceArea.countyDisplayName
      }))
    } as NestingParentDropdownItem));
  }

  // T is expected to be DropdownItem or string
  const getDefaultSelectedDropdownItems = <T, >(options: T[], selectedOptions: string[], selectAll?: boolean) => {
    if (options.length > 0) {
      if (selectAll) {
        return options;
      }
      // if it's a DropdownItem
      else if (typeof options[0] === "object") {
        return options.filter((s) => selectedOptions.includes((s as DropdownItem<string>).key)) as T[];
        // if it's a string
      } else {
        return options.filter((s) => selectedOptions.includes(s as string)) as T[];
      }
    }
    return new Array<T>();
  };

  const getDefaultSelectedNestedDropdownItems = (options: NestingParentDropdownItem[], selectedOptions: string[], selectAll?: boolean) => {
    if (selectAll) {
      return options.flatMap((t) => t.children.map((c) => c.key));
    }

    return options.flatMap((t) => {
      return t.children.filter((c) => selectedOptions.includes(c.key)).map((c) => c.key);
    });
  };

  // the emptyFilters parameter is necessary because all the set* calls made in resetFilters
  // get batched together with the ones in applyFilters, so they aren't updated by the time newApiReadyFilters is constructed
  // same reasoning goes for saveFiltersToLocalStorage
  const applyFilters = async (emptyFilters?: boolean) => {
    setExceptionsLoading(true);
    setTriggerRefreshData(true);

    // handling all this up front here so we only have to check emptyFilters once instead of 5 times
    const emptyDefaults = defaultFilterSelections;

    let availableProjectIds: number[], counties: string[], serviceAreaStatuses: RegionSetStatusType[],
      statuses: SpecialtyGroupExceptionStatus[], specialties: string[], startDate: string, endDate: string, gapResolutionStatuses: string[], exceptionGrades: SpecialtyGroupExceptionGrade[],
      lineOfBusinessIds: number[], metOverallStatusToApply: MetOverallStatus, excludedProvidersToApply: UnprocessedExcludedProviders;

    if (emptyFilters) {
      availableProjectIds = emptyDefaults.selectedProjects.map((p) => parseInt(p.key, 10));
      const serviceArea = calculateServiceAreaSelections(serviceAreaOptions, emptyDefaults.selectedServiceAreas, true);
      counties = serviceArea.counties;
      statuses = isForArchived ? [SpecialtyGroupExceptionStatus.Archived] : emptyDefaults.selectedStatuses.map((s) => s.key as SpecialtyGroupExceptionStatus);
      serviceAreaStatuses = emptyDefaults.selectedServiceAreaStatuses.map(sa => sa.key as RegionSetStatusType);
      specialties = emptyDefaults.selectedSpecialties.map((sp) => sp.key);
      startDate = null;
      endDate = null;
      gapResolutionStatuses = emptyDefaults.selectedGapResolutionStatuses.map((sgrs) => sgrs.key);
      exceptionGrades = emptyDefaults.selectedExceptionGrades.map(eg => eg.key as SpecialtyGroupExceptionGrade);
      lineOfBusinessIds = emptyDefaults.selectedLinesOfBusiness.map((line) => parseInt(line.key));
      metOverallStatusToApply = emptyDefaults.selectedMetOverall;
      excludedProvidersToApply = emptyDefaults.selectedUnprocessedExcludedProviders;
    } else {
      availableProjectIds = (filterProjects.length > 0 ? filterProjects : projectsOptions).map((p) => parseInt(p.key, 10));
      const serviceArea = calculateServiceAreaSelections(serviceAreaOptions, filterServiceArea, true);
      counties = serviceArea.counties;
      statuses = isForArchived ? [SpecialtyGroupExceptionStatus.Archived] : filterExceptionStatus.map((s) => s.key as SpecialtyGroupExceptionStatus);
      serviceAreaStatuses = filterServiceAreaStatuses.map(sa => sa.key as RegionSetStatusType);
      specialties = filterSpecialty.map((sp) => sp.key);
      startDate = filterStartDate ? filterStartDate : null;
      endDate = filterEndDate ? filterEndDate : null;
      gapResolutionStatuses = filterGapResolutionStatuses.map((s) => s.key);
      exceptionGrades = filterExceptionGrades.map(eg => eg.key as SpecialtyGroupExceptionGrade);
      lineOfBusinessIds = filterLinesOfBusiness.map((line) => parseInt(line.key));
      metOverallStatusToApply = metOverallStatus;
      excludedProvidersToApply = unprocessedExcludedProviders;
    }

    const newApiReadyFilters: ExceptionsApiFilters = {
      projectIds: availableProjectIds,
      counties,
      specialties,
      startDate,
      endDate,
      statuses,
      gapResolutionStatuses,
      exceptionGrades,
      lineOfBusinessIds
    };

    const exceptionRequestModel: ExceptionsFilterRequestModel = {
      projectIds: availableProjectIds,
      regionSetIds: counties.map((c) => parseInt(c, 10)),
      specialtyGroupCodes: specialties,
      serviceAreaStatuses,
      startDate,
      endDate,
      specialtyGroupExceptionStatus: statuses,
      metOverall: metOverallStatusToApply === MetOverallStatus.All ? undefined : metOverallStatusToApply === MetOverallStatus.Yes,
      gapResolutionStatuses,
      specialtyGroupExceptionGrades: exceptionGrades,
      pageSize: pageSize,
      pageIndex: pageIndex,
      sortExceptionColumn: sortColumn,
      sortDirection: sortDirection,
      lineOfBusinessIds: lineOfBusinessIds,
      unprocessedExcludedProviders : excludedProvidersToApply === UnprocessedExcludedProviders.All ? undefined : excludedProvidersToApply === UnprocessedExcludedProviders.Yes,
    };

    setApiReadyFilters(newApiReadyFilters);

    // trigger data refresh
    setRequestModel(exceptionRequestModel);
  }

  const clearFilters = () => {
    const defaults = defaultFilterSelections;

    setFilterServiceArea(defaults.selectedServiceAreas);
    setFilterServiceAreaStatuses(defaults.selectedServiceAreaStatuses);
    setFilterSpecialty(defaults.selectedSpecialties);
    setFilterExceptionStatus(defaults.selectedStatuses);
    setFilterProjects(defaults.selectedProjects);
    setFilterStartDate(defaults.selectedStartDate);
    setFilterEndDate(defaults.selectedEndDate);
    setMetOverallStatus(defaults.selectedMetOverall);
    setFilterGapResolutionStatuses(defaults.selectedGapResolutionStatuses);
    setFilterExceptionGrades(defaults.selectedExceptionGrades);
    setFilterLinesOfBusiness(defaults.selectedLinesOfBusiness);
    setUnprocessedExcludedProviders(defaults.selectedUnprocessedExcludedProviders)

    applyFilters(true);
  }

  const reloadFilters = () => {
    clearFilters();
    setHasLoadedFilterOptions(false);
    setUpdateFilters(true);
  }

  const applyCurrentFilterSetItems = (filterSetItems: FilterSetItem[]) => {
    setFilterProjects(filterSetItems.filter(item => item.namespacePath.startsWith("Project§"))
      .map(item => projectsOptions.find(project => project.key === item.displayName))
      .filter(item => item));
    setFilterServiceArea(filterSetItems.filter(item => item.namespacePath.startsWith("ServiceArea§")
      && serviceAreaOptions.some(serviceArea => serviceArea.children.some(serviceArea => serviceArea.key === item.displayName)))
      .map(item => item.displayName));
    setFilterSpecialty(filterSetItems.filter(item => item.namespacePath.startsWith("Specialty§"))
      .flatMap(item => specialtyOptions.filter(specialty => specialty.key === item.displayName))
      .filter(item => item));
    setFilterServiceAreaStatuses(filterSetItems.filter(item => item.namespacePath.startsWith("ServiceAreaStatus§"))
      .map(item => serviceAreaStatusOptions.find(serviceAreaStatus => serviceAreaStatus.key === item.displayName || !serviceAreaStatus.key && item.displayName === 'Blank'))
      .filter(item => item));
    if (!isForArchived) {
      setFilterExceptionStatus(filterSetItems.filter(item => item.namespacePath.startsWith("ExceptionStatus§"))
        .map(item => exceptionStatusOptions.find(exceptionStatus => exceptionStatus.key === item.displayName))
        .filter(item => item));
    } else {
      setFilterExceptionStatus([]);
    }
    setFilterGapResolutionStatuses(filterSetItems.filter(item => item.namespacePath.startsWith("GapResolutionStatus§"))
      .map(item => gapResolutionStatusOptions.find(gapResolutionStatus => gapResolutionStatus.key === item.displayName))
      .filter(item => item));
    const startDate = filterSetItems.find(item => item.namespacePath.startsWith("StartDate§"));
    setFilterStartDate(startDate?.displayName ?? '');
    const endDate = filterSetItems.find(item => item.namespacePath.startsWith("EndDate§"));
    setFilterEndDate(endDate?.displayName ?? '');
    const metOverall = filterSetItems.find(item => item.namespacePath.startsWith("MetOverallStatus§"));
    setMetOverallStatus(metOverall?.displayName as MetOverallStatus ?? MetOverallStatus.All);
    setFilterExceptionGrades(filterSetItems.filter(item => item.namespacePath.startsWith("ExceptionGrade§"))
      .map(item => exceptionGradeOptions.find(exceptionGrade => exceptionGrade.key == item.displayName))
      .filter(item => item));
    setFilterLinesOfBusiness(filterSetItems.filter(item => item.namespacePath.startsWith("LineOfBusiness§"))
      .map(item =>  lineOfBusinessOptions.find(line => line.key === item.displayName))
      .filter(item => item));
    const excludedProviders = filterSetItems.find(item => item.namespacePath.startsWith("UnprocessedExcludedProviders§"));
    setUnprocessedExcludedProviders(excludedProviders?.displayName as UnprocessedExcludedProviders ?? UnprocessedExcludedProviders.All);
  }

  const defaultFilterSelections = useMemo(() => {
    const selectedServiceAreas = getDefaultSelectedNestedDropdownItems(serviceAreaOptions, [], true);
    const selectedServiceAreaStatuses = getDefaultSelectedDropdownItems(serviceAreaStatusOptions, defaultServiceAreaStatuses, true);
    const selectedSpecialties = getDefaultSelectedDropdownItems(specialtyOptions, [], true);
    const selectedStatuses = getDefaultSelectedDropdownItems(exceptionStatusOptions, defaultExceptionStatuses, true);
    const selectedProjects = getDefaultSelectedDropdownItems(projectsOptions, [], true);
    const selectedStartDate = '';
    const selectedEndDate = '';
    const selectedGapResolutionStatuses = getDefaultSelectedDropdownItems(gapResolutionStatusOptions, defaultGapResolutionStatuses, true);
    const selectedMetOverall = MetOverallStatus.All;
    const selectedExceptionGrades = getDefaultSelectedDropdownItems(exceptionGradeOptions, defaultExceptionGrades, true);
    const selectedLinesOfBusiness = getDefaultSelectedDropdownItems(lineOfBusinessOptions, [], true);
    const selectedUnprocessedExcludedProviders = UnprocessedExcludedProviders.All;

    return {
      selectedServiceAreas,
      selectedServiceAreaStatuses,
      selectedSpecialties,
      selectedStatuses,
      selectedProjects,
      selectedStartDate,
      selectedEndDate,
      selectedMetOverall,
      selectedGapResolutionStatuses,
      selectedExceptionGrades,
      selectedLinesOfBusiness,
      selectedUnprocessedExcludedProviders
    };
  }, [serviceAreaOptions, serviceAreaStatusOptions, specialtyOptions, exceptionStatusOptions, projectsOptions, gapResolutionStatusOptions, lineOfBusinessOptions]);

  return (
    <ExceptionsFiltersContext.Provider
      value={{
        apiReadyFilters,
        applyCurrentFilterSetItems,
        applyDisabled,
        applyFilters,
        clearFilters,
        reloadFilters,
        exceptionStatusOptions,
        exceptionsLoading,
        filterEndDate,
        filterExceptionStatus,
        filterGapResolutionStatuses,
        filterExceptionGrades,
        filterLinesOfBusiness,
        filterProjects,
        filterServiceArea,
        filterServiceAreaStatuses,
        filterSpecialty,
        filterStartDate,
        gapResolutionStatusOptions,
        exceptionGradeOptions,
        hasLoadedFilterOptions,
        hasSetInitialExceptionStatuses,
        isForArchived,
        lineOfBusinessOptions,
        metOverallStatus,
        pageIndex,
        pageSize,
        projectsOptions,
        resetToFirstPage,
        requestModel,
        selectedExceptionIds,
        setSelectedExceptionIds,
        serviceAreaOptions,
        serviceAreaStatusOptions,
        setFilterEndDate,
        setExceptionsLoading,
        setFilterExceptionStatus,
        setFilterExceptionGrades,
        setFilterGapResolutionStatuses,
        setFilterLinesOfBusiness,
        setFilterProjects,
        setFilterServiceArea,
        setFilterServiceAreaStatuses,
        setFilterStartDate,
        setFilterSpecialty,
        setHasLoadedFilterOptions,
        setHasSetInitialExceptionStatuses,
        setIsForArchived,
        setMetOverallStatus,
        setPageIndex,
        setPageSize,
        setResetToFirstPage,
        setRequestModel,
        setSortColumn,
        setSortDirection,
        setUpdateFilters,
        setTriggerRefreshData,
        setUnprocessedExcludedProviders,
        sortColumn,
        sortDirection,
        specialtyOptions,
        triggerRefreshData,
        unprocessedExcludedProviders,
        validStartDate,
        validEndDate,
        customProjectName,
        setCustomProjectName,
        enableRegulatoryExceptionReports,
        setEnableRegulatoryExceptionReports,
        haveRemovedServiceArea,
        setHaveRemovedServiceArea,
        haveRemovedSpecialty,
        setHaveRemovedSpecialty
      }}>
      {props.children}
    </ExceptionsFiltersContext.Provider>
  )
}

export default ExceptionsFiltersContextProvider;
